// libraries
import React, { useEffect, useState } from 'react';
import ReactLoading from 'react-loading';
import { useSelector } from 'react-redux';

// dnd-kit
import {
    DndContext,
    PointerSensor,
    KeyboardSensor,
    useSensor,
    useSensors,
    closestCenter,
    DragStartEvent,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    SortableContext,
    useSortable,
    arrayMove,
    rectSortingStrategy,
    sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';

import { CSS } from '@dnd-kit/utilities';

// js
import {
    Container,
    Home,
    ContainerAuthors,
    ContainerInner,
    ContainerDividerScreen,
    Header,
    ContainerSelect,
    Option,
    ButtonCreateContent,
    ContainerWrap,
    ContainerListEmpty,
    ContainerPhoto,
    NameCategories,
} from './mediasContentStyles';
import contents from '../../../services/contents';
import { RootState } from '../../../store';
import medias from '../../../services/medias';
import colors from '../../../styles/colors';
import mediasContentsConstants from './mediasContentsConstants';

// components
import MenuComponent from '../../../components/Menu/MenuComponent';
import LoadingComponent from '../../../components/Loading/LoadingComponent';
import ModalCreateOrEditMedias from './components/ModalCreateOrEditMedias';

const MediasContentsPage: React.FC = () => {
    // useSelector
    const infoUser = useSelector((state: RootState) => state.user.user);

    // states
    const [allContents, setAllContents] = useState<Array<any>>([]);
    const [allMedias, setAllMedias] = useState<Array<any>>([]);
    const [items, setItems] = useState<Array<any>>([]); // estado usado pelo DnD
    const [contentSelected, setContentSelected] = useState<any>(null);
    const [visibleDrawer, setVisibleDrawer] = useState(false);
    const [validationEmpty, setValidationEmpty] = useState(true);
    const [loading, setLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [inputs, setInputs] = useState(mediasContentsConstants.initialInputs);
    const [idMediaActual, setIdMediaActual] = useState('');
    const [mode, setMode] = useState('post');
    const [draggingId, setDraggingId] = useState<string | null>(null);
    const [loadingDnd, setLoadingDnd] = useState(false);

    // dnd sensors
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    // methods
    const ActionGetContents = async () => {
        const GetContents = await contents.GetAllContents('', '', '', '', infoUser?.token);
        if (GetContents?.status === 200) {
            setAllContents(GetContents?.result || []);
        }
    };

    const ActionGetMedias = async () => {
        const GetMedias = await medias.GetAllMedias(contentSelected?.id, infoUser?.token);
        if (GetMedias?.status === 200) {
            setValidationEmpty(GetMedias.result.length > 0);
            setAllMedias(GetMedias?.result || []);
        }
    };

    const onChangeFormat = (txt: React.ChangeEvent<HTMLSelectElement>) => {
        setContentSelected({ id: txt.target.value });
    };

    const ToggleModal = () => {
        if (allMedias.length === 0) {
            const inputsAddWithoutMedias = {
                title: contentSelected?.title,
                description: contentSelected?.description,
                cover: contentSelected?.cover,
                media: mediasContentsConstants.initialInputs.media,
                idMedia: mediasContentsConstants.initialInputs.idMedia,
                text: mediasContentsConstants.initialInputs.text,
                type: mediasContentsConstants.initialInputs.type,
                orientationHorizontal: mediasContentsConstants.initialInputs.orientationHorizontal,
            };
            setInputs(inputsAddWithoutMedias);
        } else {
            const inputsAddWithMedias = {
                title: allMedias[0].title,
                description: allMedias[0].description,
                cover: allMedias[0].cover,
                media: mediasContentsConstants.initialInputs.media,
                idMedia: mediasContentsConstants.initialInputs.idMedia,
                text: mediasContentsConstants.initialInputs.text,
                type: mediasContentsConstants.initialInputs.type,
                orientationHorizontal: mediasContentsConstants.initialInputs.orientationHorizontal,
            };
            setInputs(inputsAddWithMedias);
        }

        setIsOpen(!isOpen);
    };

    const persistReorder = async (position: number, idMedia: string) => {
        try {
            setLoadingDnd(true);
            await medias.EditPositionMedia(position, idMedia, infoUser?.token);
            setLoadingDnd(false);
            ActionGetMedias();
        } catch (e) {
            setLoadingDnd(false);
        }
    };

    // useEffect
    useEffect(() => {
        ActionGetContents();
    }, []);

    useEffect(() => {
        if (contentSelected) {
            ActionGetMedias();
        }
    }, [contentSelected]);

    useEffect(() => {
        setItems(allMedias || []);
    }, [allMedias]);

    // dnd handlers
    const onDragStart = (event: DragStartEvent) => {
        setDraggingId(String(event.active.id));
    };

    const onDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;
        setDraggingId(null);
        if (!over || active.id === over.id) return;

        const oldIndex = items.findIndex(m => String(m.id) === String(active.id));
        const newIndex = items.findIndex(m => String(m.id) === String(over.id));
        const newOrder = arrayMove(items, oldIndex, newIndex);

        setItems(newOrder);
        setAllMedias(newOrder);

        await persistReorder(newIndex, String(active.id));
    };

    function SortableCard({ item, onOpen, isDragging }: { item: any; onOpen: () => void; isDragging: boolean }) {
        const { attributes, listeners, setNodeRef, transform, transition, isSorting } = useSortable({
            id: String(item.id),
        });

        const style: React.CSSProperties = {
            transform: CSS.Transform.toString(transform),
            transition,
            zIndex: isSorting ? 999 : 'auto',
            cursor: isSorting ? 'grabbing' : 'default',
            flex: '0 0 auto',
        };

        const onHandleMouseDown: React.MouseEventHandler = e => e.stopPropagation();

        return (
            <div ref={setNodeRef} style={style}>
                <ContainerPhoto
                    background={item.cover}
                    onClick={!isDragging ? onOpen : undefined}
                    style={{
                        width: 260,
                        height: 160,
                        position: 'relative',
                        borderRadius: 12,
                        overflow: 'hidden',
                    }}
                >
                    {/* Drag handle */}
                    <button
                        {...attributes}
                        {...listeners}
                        onMouseDown={onHandleMouseDown}
                        aria-label="Arrastar"
                        title="Arrastar para reordenar"
                        style={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            width: 28,
                            height: 28,
                            borderRadius: 6,
                            border: 'none',
                            background: 'rgba(0,0,0,0.35)',
                            color: '#fff',
                            fontSize: 16,
                            display: 'grid',
                            placeItems: 'center',
                            cursor: 'grab',
                            userSelect: 'none',
                        }}
                        disabled={loadingDnd}
                    >
                        {loadingDnd ? (
                            <ReactLoading type="spin" color={colors.basic.white} height="20px" width="20px" />
                        ) : (
                            '⠿'
                        )}
                    </button>

                    <NameCategories
                        style={{
                            position: 'absolute',
                            left: 10,
                            bottom: 10,
                            right: 10,
                            color: '#fff',
                            textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                            fontWeight: 600,
                        }}
                    >
                        {item.title}
                    </NameCategories>
                </ContainerPhoto>
            </div>
        );
    }

    // renders
    const RenderMedias = () => {
        if (items.length > 0 && validationEmpty) {
            return (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={onDragStart}
                    onDragEnd={onDragEnd}
                >
                    <SortableContext items={items.map(i => String(i.id))} strategy={rectSortingStrategy}>
                        <ContainerWrap
                            style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 12,
                                alignItems: 'flex-start',
                            }}
                        >
                            {items.map(v => {
                                const ActionEditCategories = () => {
                                    ToggleModal();
                                    setMode('put');
                                    setInputs({
                                        ...inputs,
                                        description: v.description,
                                        title: v.title,
                                        cover: v.cover,
                                        media: v.media,
                                        idMedia: v.id,
                                        text: v.text,
                                        orientationHorizontal: v.orientation !== 'vertical',
                                    });
                                    setIdMediaActual(v.id);
                                };

                                return (
                                    <SortableCard
                                        key={v.id}
                                        item={v}
                                        onOpen={ActionEditCategories}
                                        isDragging={draggingId === String(v.id)}
                                    />
                                );
                            })}
                        </ContainerWrap>
                    </SortableContext>
                </DndContext>
            );
        }
        if (!items.length && !validationEmpty) {
            return (
                <ContainerListEmpty>
                    <Header fontSize="32px" fontWeight="bold" color={colors.basic.gray}>
                        Olá!
                    </Header>
                    <Header fontSize="32px" color={colors.basic.gray}>
                        Defina uma opção a partir dos conteúdos existentes
                    </Header>
                    <Header fontSize="32px" color={colors.basic.gray}>
                        acima para editar uma mídia.
                    </Header>
                </ContainerListEmpty>
            );
        }
        return null;
    };

    return (
        <Container>
            <Home>
                <MenuComponent setVisibleDrawer={setVisibleDrawer} />
                {visibleDrawer ? null : (
                    <ContainerInner>
                        <ContainerAuthors>
                            <ContainerDividerScreen>
                                <Header marginBottom="30px">
                                    {'Conteúdos > '}
                                    <Header fontWeight="bold">Mídias</Header>
                                </Header>
                                <ContainerSelect onChange={onChangeFormat}>
                                    <Option value="">Selecione um conteúdo</Option>
                                    {allContents?.map(v => (
                                        <Option key={v?.id} value={v?.id}>
                                            {v?.title}
                                        </Option>
                                    ))}
                                </ContainerSelect>
                            </ContainerDividerScreen>
                            {contentSelected?.id ? (
                                <ButtonCreateContent
                                    onClick={() => {
                                        ToggleModal();
                                        setMode('post');
                                    }}
                                >
                                    +
                                </ButtonCreateContent>
                            ) : null}
                            {ModalCreateOrEditMedias(
                                isOpen,
                                setIsOpen,
                                inputs,
                                setInputs,
                                ToggleModal,
                                idMediaActual,
                                setIdMediaActual,
                                infoUser,
                                ActionGetMedias,
                                setLoading,
                                setVisibleDrawer,
                                (allContents || []).find(c => c.id === contentSelected?.id),
                                mode,
                            )}
                        </ContainerAuthors>
                        {RenderMedias()}
                    </ContainerInner>
                )}
            </Home>
            <LoadingComponent
                label="Carregando...."
                loading={loading}
                subtitle="Esta operação pode levar alguns minutos dependendo do tamanho do seu arquivo ou velocidade da sua internet..."
            />
        </Container>
    );
};

export default MediasContentsPage;
